import intersection from 'lodash/intersection';

import { getPoweredOnState } from '@/comp/StationControlCenter/SccUtils';
import { compressorTypes, DeviceType } from '@/constants/deviceTypeEnum';
import { OnOffEnum, ProactiveEngineStateEnum } from '@/constants/enums';
import { DeviceAllowedActions } from '@/constants/enums';
import { MetricType } from '@/constants/metric-type.enum';
import { useStores } from '@/hooks/use-stores';
import { permissions } from '@/constants';

export const getDisableReason = (
  isProactiveEngineRunning: boolean,
  powerChangeSupportedAction: boolean,
  isPowerChangeAllowed: boolean,
) => {
  if (isProactiveEngineRunning) return 'Function is disabled since Engine Mode is Activated';
  if (!powerChangeSupportedAction)
    return 'Function is disabled since changing power state is not supported on this compressor.';
  if (!isPowerChangeAllowed) return 'You are not allowed to change power state for devices.';
};

export const usePowerOnOffVisibility = (deviceId: string) => {
  const { devicesStore } = useStores();
  const deviceMetadata = devicesStore.deviceMetadataFromDeviceId(deviceId);
  const poweredOnMetricPresent = deviceMetadata?.metrics?.find(metric => metric === MetricType.PoweredOn);
  const powerChangeSupportedAction =
    intersection(deviceMetadata?.actions, Object.values(DeviceAllowedActions)).length === 2;

  return {
    poweredOnMetricPresent,
    powerChangeSupportedAction,
  };
};

export const usePowerOnOff = (deviceId: string, deviceType: DeviceType, poweredOn: OnOffEnum) => {
  const { permissionStore, proactiveEngineStore } = useStores();
  const { poweredOnMetricPresent, powerChangeSupportedAction } = usePowerOnOffVisibility(deviceId);
  const poweredOnState = getPoweredOnState(poweredOn);

  const isProactiveEngineRunning = proactiveEngineStore.proactiveEngineState === ProactiveEngineStateEnum.Activated;
  const isPowerChangeAllowed = permissionStore.isAuth(permissions.CONTROL_DEVICE);

  const shouldDisplayPowerOnOff =
    poweredOnMetricPresent &&
    powerChangeSupportedAction &&
    (compressorTypes.includes(deviceType) || deviceType === DeviceType.Booster);

  const isPoweredOnDisabled = isProactiveEngineRunning || !powerChangeSupportedAction || !isPowerChangeAllowed;
  const isStopButtonDisabled = isPoweredOnDisabled || !poweredOnState;
  const isStartButtonDisabled = isPoweredOnDisabled || poweredOnState;

  const disableReason = getDisableReason(isProactiveEngineRunning, powerChangeSupportedAction, isPowerChangeAllowed);

  return {
    shouldDisplayPowerOnOff,
    isPoweredOnDisabled,
    isStopButtonDisabled,
    isStartButtonDisabled,
    disableReason,
  };
};
